import React, { Dispatch, useState, useRef, useEffect, useMemo } from 'react';
import { DebouncedFunc } from 'lodash';

import '../App.css';
import NodeWrapper from './NodeWrapper'
import { useDrop } from 'react-dnd'
import { InjectNode, COA } from '../Classes1';
import { IInject, IInjectNode, IScenario, ICOA } from '../interfaces';
import { ItemTypes } from '../ItemTypes'
import Modal from 'react-modal';

interface TurnProps {
    setInjectComponents: Dispatch<any>;
    setInjectItem: Dispatch<any>;
    injectItem: IInject;
    injectComponents: IInject[];
    turn: number;
    greedy: boolean;
    updateShowHide: Dispatch<any>;
    zoomLevel: number;
    selectedScenario: IScenario;
    setNodeTurnHeight: Dispatch<any>;
    nodeTurnHeight: string;
    saveScenario: (i: IInject[]) => Promise<void>;
    setNodeSaving: Dispatch<any>;
    onChangeSave: DebouncedFunc<any>;
    nodeSaving: { injectId: string, nodeId: string, saving: boolean };
    addTurn? : Dispatch<any>;
    setSelectedScenario: Dispatch<any>;
    setNodeTurnHeightArray: Dispatch<any>;
    nodeTurnHeightArray: any;
  }

  export interface InjectState {
    hasDropped: boolean
    hasDroppedOnChild: boolean;
  }

  export interface DropItem {
    id: string;
    name: string;
  }

  Modal.setAppElement('#root');

  const customStyles = {
    content: {
      background: '#323544',
      top: '50%',
      left: '50%',
      right: 'auto',
      bottom: 'auto',
      marginRight: '-50%',
      padding: '0',
      border: 'none',
      width: '480px',
      transform: 'translate(-50%, -50%)',
    }
  };

  const NodeTurn: React.FC<TurnProps> = ({ setInjectComponents, injectComponents, turn, greedy, updateShowHide, setInjectItem, injectItem, zoomLevel, selectedScenario, nodeTurnHeight, setNodeTurnHeight, setNodeSaving, nodeSaving, onChangeSave, addTurn, setSelectedScenario, saveScenario, setNodeTurnHeightArray, nodeTurnHeightArray }) => {
    const valueRef = useRef<IInject[]>([])
    useEffect(() => {
        valueRef.current = injectComponents;
    });

    const injectComponentsRef = useRef<IInject[]>([])
    useEffect(() => {
        injectComponentsRef.current = injectComponents;
    });

    const [dropModalIsOpen, setDropModalIsOpen] = useState<boolean>(false);
    const [dropItem, setDropItem] = useState<DropItem>({ id: '', name: ''});

    const openDropModal = () => {
      setDropModalIsOpen(true);
    }
  
    const closeDropModal = () => {
      setDropModalIsOpen(false);
    }

    const clickfinishDrop = () => {
      finishDrop(null);
      closeDropModal();
    }

    const finishDrop = (_item: DropItem | null) => {
      const localItem = _item || dropItem;

      const newNode = new InjectNode();
      newNode.description = ''
      newNode.name = 'Default Node ' + (injectItem.nodes.length + 1)
      newNode.timeToLive = null;
      newNode.turn = turn;
      newNode.isNew = true;
      newNode.startTurn = turn;

      if (localItem.name !== 'inject-end-node') {
        const coaIds = injectItem.nodes.reduce<number[]>((ids, n) => ids.concat(n.coas.map(({id}) => id)), [])
        const nextCoaId = Math.max(...coaIds) + 1;
        newNode.coas.push(new COA({
          id: nextCoaId,
          isNew: true,
        }))
      }

      if (injectItem.nodes.length === 0) {
        newNode.id = '1';
      } else {
        const nodeIds = injectItem.nodes.map(({id}) => parseInt(id, 10));
        newNode.id = (Math.max(...nodeIds) + 1).toString()
      }

      setNodeSaving({ injectId: injectItem.injectId, nodeId: newNode.id, saving: true });

      const existingNode = injectItem.nodes.find((n) => n.id === localItem.id);
      const nodes = injectItem.nodes.slice();
      if (!existingNode) {
        nodes.push(newNode);
      }
      else {
        if (!_item) {
          const coaToUpdate = existingNode.coas.find(
            (coa) => coa.nextNodeId === dropItem.id
          );
          if (coaToUpdate && turn > existingNode.turn) {
            coaToUpdate.nextNodeId = '';
          }
          if (
            (existingNode.timeToLive !== null)
            && (dropItem.id === existingNode.timeToLive.nextNodeId)
            && (turn <= existingNode.turn)
          ) {
            existingNode.timeToLive.nextNodeId = '';
          }
        }
        existingNode.turn = turn;
        existingNode.startTurn = turn;
      }
      const turnLength = nodes.reduce<number>((len, n) => Math.max(len, n.turn), 1)

      const endTurns = injectComponents.map((i) => i.startTurn - 1 + i.turnLength);
      const maxTurn = Math.max(...endTurns, injectItem.startTurn - 1 + turnLength);
      if (maxTurn > selectedScenario.gameTimeInterval) {
        setSelectedScenario({
          ...selectedScenario,
          gameTimeInterval: maxTurn
        });
      }

      setTimeout(() => {
        console.log(turnLength);
        setInjectItem({
          ...injectItem,
          turnLength,
          nodes,
        });

        // Need this to wait for setInjectItem to resolve...
        setTimeout(() => {
          const injUpdates = injectComponents.map((inject: IInject) => {
            if (inject.id === injectItem.id) {
                return {
                  ...inject,
                  turnLength,
                  nodes,
                };
            } else {
                return structuredClone(inject);
            }
          })
          setInjectComponents(injUpdates);
          saveScenario(injUpdates);
        })
      })
    }

    /*** DnD Drop Inject in Turn Code */
    const [{ isOver, isOverCurrent }, drop] = useDrop(
        () => ({
          accept: [ItemTypes.BOX, ItemTypes.INJECT, ItemTypes.INJECT_END],
          drop(_item: DropItem, monitor) {
            const didDrop = monitor.didDrop()
            if (didDrop && !greedy) {
              return
            }

            if (turn === 1 && injectItem.nodes.some(n => n.turn === 1)) {
              return;
            }

            let noDeletes: boolean = true;
            let dragNodes: IInjectNode[] = JSON.parse(JSON.stringify(injectItem.nodes));
            dragNodes.forEach((node: IInjectNode) => {
              node.coas.forEach((coa: ICOA) => {
                if (_item.id === coa.nextNodeId) {
                  if (turn <= node.turn) {
                    setDropItem(_item)
                    openDropModal()
                    noDeletes = false;
                    return;
                  }
                }
              })
              if (node.timeToLive !== null) {
                if (_item.id === node.timeToLive.nextNodeId) {
                  if (turn <= node.turn) {
                    setDropItem(_item)
                    openDropModal()
                    noDeletes = false;
                    return;
                  }
                }
              }
            });

            if (noDeletes) {
              finishDrop(_item)
            }
            onChangeSave();
          },
          collect: (monitor) => ({
            isOver: monitor.isOver(),
            isOverCurrent: monitor.isOver({ shallow: true }),
            canDrop: monitor.canDrop(),
          })
        }),
        [injectItem]
      )

      let borderRight = '1px dashed #fff'
      let border = 'inherit'
    
      if (turn !== 1 && (isOverCurrent || (isOver && greedy))) {
        border = '2px solid #3F83FC'
        borderRight = '2px solid #3F83FC'
      }

    /*** DnD Drop Inject in Turn Code End */

   const moveInject = (dragIndex: number, hoverIndex: number) => {
      let injArr: IInject[] = JSON.parse(JSON.stringify(injectComponentsRef.current));
      let injItem = injArr.splice(dragIndex, 1);
      injArr.splice(hoverIndex, 0, injItem[0]);
      setInjectComponents(injArr)
    }

    const addTurnNode = useMemo(() => turn === injectItem.turnLength + 1, [injectItem.turnLength, turn]);

    return (
        <div ref={drop}  data-testid={`timeline${turn}`} className={`turn turn${turn} node-turn ${addTurnNode ? 'add-node-turn' : ''}`} style={{ 'border': border, borderRight: borderRight, height: parseInt(nodeTurnHeight) <= 850 || nodeTurnHeight === '' ? 'auto' : nodeTurnHeight }} onClick={addTurn}>
            {
              // Check if after last turn
              addTurnNode
              ? <div className="add-turn-text">Add a Turn +</div>
              : <div className="turn-label">Turn {turn}</div>
            }
            { !addTurnNode &&
              injectItem.nodes.filter(
                (item) => item.turn === turn
              ).map((item: IInjectNode, i: number) => {
                return (
                    <div key={item.id}>

                          <NodeWrapper setInjectComponents={setInjectComponents} injectComponents={injectComponents} setInjectItem={setInjectItem} injectItem={injectItem} moveInject={moveInject} turn={turn} id={item.id} key={item.id} i={i} index={i} zoomLevel={zoomLevel} selectedScenario={selectedScenario} node={item} setNodeTurnHeight={setNodeTurnHeight} nodeSaving={nodeSaving} saveScenario={saveScenario} onChangeSave={onChangeSave} nodeTurnHeightArray={nodeTurnHeightArray} setNodeTurnHeightArray={setNodeTurnHeightArray} />

                    </div>
                )
            })}

            <Modal
                isOpen={dropModalIsOpen}
                onRequestClose={closeDropModal}
                style={customStyles}
                contentLabel="Node Drop Modal"
            >
                <div className="new-modal-header">Node Line Removal <img src="/img/light-grey-x.svg" className="new-modal-padding-close" alt="x" onClick={closeDropModal}></img></div>
                
                <div className="new-modal-text">The Node you are trying to move contains connections that can no longer be maintained and will be removed if you proceed.  Would you like to proceed?</div>

                <button onClick={closeDropModal} className="new-cancel-btn"><span>Cancel</span></button>
                <button onClick={clickfinishDrop} className="new-confirm-btn">
                    <span>Proceed</span>
                </button>
            </Modal>
        </div>
    )
}

export default NodeTurn;